import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static RESEND_COOLDOWN_MS = 60000
  static RESEND_COUNTDOWN_SECONDS = 60
  static OTP_LENGTH = 6

  static targets = ["emailInput", "sendButton", "otpSection", "otpInput", "submitButton", "message", "resendButton", "countdown"]
  static values = {
    applicationId: String,
    sendUrl: String,
    verifyUrl: String,
    autoSend: { type: Boolean, default: false },
    nextStepUrl: String
  }

  connect() {
    this.countdownInterval = null
    this.codeSent = false
    this.isVerifying = false

    if (this.autoSendValue && !this.codeSent) {
      this.autoSendCode()
    }

    if (this.hasOtpInputTarget && this.otpInputTargets[0]) {
      this.otpInputTargets[0].focus()
    }

    if (!this.hasSubmitButtonTarget) {
      this.externalSubmitButton = document.getElementById("verify-continue-button")
      if (this.externalSubmitButton) {
        this.externalSubmitButton.form?.addEventListener("submit", this.handleFormSubmit.bind(this))
      }
    }

    this.updateSubmitButtonState()
  }

  disconnect() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  async autoSendCode() {
    const lastSentKey = `otp_sent_${this.applicationIdValue}`
    const lastSent = sessionStorage.getItem(lastSentKey)
    const now = Date.now()

    if (lastSent && (now - parseInt(lastSent)) < this.constructor.RESEND_COOLDOWN_MS) {
      const elapsed = Math.floor((now - parseInt(lastSent)) / 1000)
      const remaining = this.constructor.RESEND_COUNTDOWN_SECONDS - elapsed
      if (remaining > 0) {
        this.startResendCountdown(remaining)
        return
      }
    }

    await this.sendCodeRequest()
    sessionStorage.setItem(lastSentKey, now.toString())
  }

  async sendCode(event) {
    if (event) event.preventDefault()

    if (this.hasEmailInputTarget) {
      const email = this.emailInputTarget.value
      if (!email || !email.includes("@")) {
        this.showMessage("Please enter a valid email address", "error")
        return
      }
    }

    await this.sendCodeRequest()
  }

  async sendCodeRequest() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
      this.sendButtonTarget.innerHTML = "Sending..."
    }

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true
    }

    try {
      const response = await post(this.sendUrlValue, {
        body: JSON.stringify({ application_id: this.applicationIdValue }),
        contentType: "application/json",
        responseKind: "json"
      })

      const data = await response.json

      if (data.status === "success") {
        this.codeSent = true
        this.showOtpSection()
        this.showMessage("Verification code sent! Check your email.", "success")
        this.startResendCountdown()
      } else {
        this.showMessage(data.message || "Failed to send code", "error")
        this.resetSendButton()
      }
    } catch (error) {
      this.showMessage("Network error. Please try again.", "error")
      this.resetSendButton()
    }
  }

  async handleFormSubmit(event) {
    if (this.isVerified) return true

    event.preventDefault()
    await this.verifyCode(event)
  }

  async verifyCode(event) {
    if (event) event.preventDefault()

    const code = this.otpInputTargets.map(input => input.value).join("")
    if (code.length !== this.constructor.OTP_LENGTH) {
      this.showMessage(`Please enter the complete ${this.constructor.OTP_LENGTH}-digit code`, "error")
      return
    }

    if (this.isVerifying) return
    this.isVerifying = true

    const button = this.getSubmitButton()
    if (button) {
      button.disabled = true
      button.value = "Verifying..."
    }

    try {
      const response = await post(this.verifyUrlValue, {
        body: JSON.stringify({
          application_id: this.applicationIdValue,
          code: code
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      const data = await response.json

      if (data.status === "success") {
        this.isVerified = true
        this.showMessage("Email verified successfully!", "success")

        if (button) {
          button.value = "Verified! Continuing..."
          button.classList.remove("bg-gray-400", "disabled:bg-gray-400")
          button.classList.add("bg-green-600")
        }

        sessionStorage.removeItem(`otp_sent_${this.applicationIdValue}`)

        setTimeout(() => {
          if (button && button.form) {
            button.form.removeEventListener("submit", this.handleFormSubmit.bind(this))
            button.form.submit()
          } else {
            window.location.reload()
          }
        }, 500)
      } else {
        this.showMessage(data.message || "That code didn't work. Please try again.", "error")
        this.showOtpError()
        this.isVerifying = false
        this.updateSubmitButtonState()
      }
    } catch (error) {
      this.showMessage("Network error. Please try again.", "error")
      this.showOtpError()
      this.isVerifying = false
      this.updateSubmitButtonState()
    }
  }

  getSubmitButton() {
    if (this.hasSubmitButtonTarget) return this.submitButtonTarget
    return this.externalSubmitButton
  }

  isCodeComplete() {
    return this.hasOtpInputTarget &&
           this.otpInputTargets.length === this.constructor.OTP_LENGTH &&
           this.otpInputTargets.every(input => input.value.length === 1)
  }

  updateSubmitButtonState() {
    const button = this.getSubmitButton()
    if (!button) return

    // Skip if button is disabled by edit-email controller
    if (button.classList.contains("pointer-events-none")) return

    if (this.isCodeComplete() && !this.isVerifying) {
      button.disabled = false
      button.value = "Verify & Continue"
    } else {
      button.disabled = true
      button.value = "Verify & Continue"
    }
  }

  showOtpSection() {
    if (this.hasOtpSectionTarget) {
      this.otpSectionTarget.classList.remove("hidden")
    }

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.innerHTML = "Code Sent"
      this.sendButtonTarget.classList.add("bg-green-600")
      this.sendButtonTarget.classList.remove("bg-blue-600", "hover:bg-blue-700")
    }

    if (this.hasOtpInputTarget && this.otpInputTargets[0]) {
      this.otpInputTargets[0].focus()
    }
  }

  resetSendButton() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false
      this.sendButtonTarget.innerHTML = "Send Code"
    }

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = false
    }
  }

  startResendCountdown(initialSeconds = this.constructor.RESEND_COUNTDOWN_SECONDS) {
    let seconds = initialSeconds

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true
    }

    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = `(${seconds}s)`
    }

    this.countdownInterval = setInterval(() => {
      seconds--

      if (this.hasCountdownTarget) {
        this.countdownTarget.textContent = `(${seconds}s)`
      }

      if (seconds <= 0) {
        clearInterval(this.countdownInterval)

        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.disabled = false
        }

        if (this.hasCountdownTarget) {
          this.countdownTarget.textContent = ""
        }
      }
    }, 1000)
  }

  resendCode(event) {
    if (event) event.preventDefault()
    sessionStorage.removeItem(`otp_sent_${this.applicationIdValue}`)
    this.sendCodeRequest()
  }

  handleOtpInput(event) {
    const input = event.target
    const index = this.otpInputTargets.indexOf(input)

    this.clearOtpError()
    input.value = input.value.replace(/[^0-9]/g, "")

    if (input.value.length === 1 && index < this.otpInputTargets.length - 1) {
      this.otpInputTargets[index + 1].focus()
    }

    this.updateSubmitButtonState()
  }

  handleOtpKeydown(event) {
    const input = event.target
    const index = this.otpInputTargets.indexOf(input)

    if (event.key === "Backspace" && input.value === "" && index > 0) {
      this.otpInputTargets[index - 1].focus()
    }

    if (event.key === "Enter") {
      event.preventDefault()
      if (this.isCodeComplete()) {
        this.verifyCode()
      }
    }

    setTimeout(() => this.updateSubmitButtonState(), 0)
  }

  handleOtpPaste(event) {
    event.preventDefault()
    const paste = (event.clipboardData || window.clipboardData).getData("text")
    const digits = paste.replace(/[^0-9]/g, "").slice(0, this.constructor.OTP_LENGTH)

    digits.split("").forEach((digit, index) => {
      if (this.otpInputTargets[index]) {
        this.otpInputTargets[index].value = digit
      }
    })

    this.updateSubmitButtonState()

    if (digits.length < this.constructor.OTP_LENGTH) {
      const nextEmpty = this.otpInputTargets.find(input => !input.value)
      if (nextEmpty) {
        nextEmpty.focus()
      }
    } else {
      this.otpInputTargets[this.constructor.OTP_LENGTH - 1].focus()
    }
  }

  clearOtpInputs() {
    this.otpInputTargets.forEach(input => input.value = "")
    if (this.otpInputTargets[0]) {
      this.otpInputTargets[0].focus()
    }
    this.updateSubmitButtonState()
  }

  showOtpError() {
    this.otpInputTargets.forEach(input => {
      input.classList.remove("border-gray-300", "focus:ring-black", "focus:border-black")
      input.classList.add("border-red-500", "text-red-600", "focus:ring-red-500", "focus:border-red-500", "bg-red-50")
    })
  }

  clearOtpError() {
    this.otpInputTargets.forEach(input => {
      input.classList.remove("border-red-500", "text-red-600", "focus:ring-red-500", "focus:border-red-500", "bg-red-50")
      input.classList.add("border-gray-300", "focus:ring-black", "focus:border-black")
    })

    if (this.hasMessageTarget && this.messageTarget.classList.contains("bg-red-50")) {
      this.messageTarget.classList.add("hidden")
    }
  }

  showMessage(text, type) {
    if (!this.hasMessageTarget) return

    this.messageTarget.classList.remove("hidden", "bg-green-50", "text-green-800", "border-green-200", "bg-red-50", "text-red-800", "border-red-200")

    if (type === "success") {
      this.messageTarget.textContent = text
      this.messageTarget.classList.add("bg-green-50", "text-green-800", "border-green-200")
    } else {
      const xIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="flex-shrink-0 mt-2">
        <path d="M11.8148 10L18.6082 3.20664C18.7308 3.08824 18.8285 2.94662 18.8958 2.79003C18.9631 2.63345 18.9985 2.46503 19 2.29461C19.0014 2.1242 18.969 1.95519 18.9044 1.79746C18.8399 1.63973 18.7446 1.49642 18.6241 1.37592C18.5036 1.25541 18.3603 1.16011 18.2025 1.09557C18.0448 1.03104 17.8758 0.998568 17.7054 1.00005C17.535 1.00153 17.3666 1.03694 17.21 1.1042C17.0534 1.17147 16.9118 1.26924 16.7934 1.39183L10 8.18519L3.20664 1.39183C2.96458 1.15803 2.64037 1.02867 2.30385 1.03159C1.96733 1.03452 1.64542 1.1695 1.40746 1.40746C1.1695 1.64542 1.03452 1.96733 1.03159 2.30385C1.02867 2.64037 1.15803 2.96458 1.39183 3.20664L8.18519 10L1.39183 16.7934C1.26924 16.9118 1.17147 17.0534 1.1042 17.21C1.03694 17.3666 1.00153 17.535 1.00005 17.7054C0.998568 17.8758 1.03104 18.0448 1.09557 18.2025C1.16011 18.3603 1.25541 18.5036 1.37592 18.6241C1.49642 18.7446 1.63973 18.8399 1.79746 18.9044C1.95519 18.969 2.1242 19.0014 2.29461 19C2.46503 18.9985 2.63345 18.9631 2.79003 18.8958C2.94662 18.8285 3.08824 18.7308 3.20664 18.6082L10 11.8148L16.7934 18.6082C17.0354 18.842 17.3596 18.9713 17.6961 18.9684C18.0327 18.9655 18.3546 18.8305 18.5925 18.5925C18.8305 18.3546 18.9655 18.0327 18.9684 17.6961C18.9713 17.3596 18.842 17.0354 18.6082 16.7934L11.8148 10Z" fill="#C82659"/>
      </svg>`
      this.messageTarget.innerHTML = `<div class="flex items-start gap-2">${xIcon}<span>${text}</span></div>`
      this.messageTarget.classList.add("bg-red-50", "text-red-800", "border-red-200")
    }

    if (type === "success") {
      setTimeout(() => {
        this.messageTarget.classList.add("hidden")
      }, 5000)
    }
  }
}
